import i18next from 'i18next';
import type { Resource } from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';

import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/de-at';
import 'dayjs/locale/fr';
import 'dayjs/locale/hu';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import 'dayjs/locale/ro';
import 'dayjs/locale/sk';

export const USER_PREFERRED_CULTURE_STORAGE_KEY = 'UserPrefferedCulture';

const DEFAULT_CULTURE = 'en-US';

const FALLBACK_LANGUAGE = 'en';

const DAYJS_LOCALES = new Set(['cs', 'de', 'de-at', 'en', 'fr', 'hu', 'it', 'nl', 'ro', 'sk']);

const localeModules = import.meta.glob<Record<string, string>>('./locales/*.json', {
  eager: true,
  import: 'default',
});

function toLanguageCode(fileName: string): string {
  const [language = fileName, region] = fileName.split('-');
  return region ? `${language}-${region.toUpperCase()}` : language;
}

function buildResources(): Resource {
  const resources: Resource = {};
  for (const [path, translation] of Object.entries(localeModules)) {
    const fileName = path.replace('./locales/', '').replace(/\.json$/, '');
    resources[toLanguageCode(fileName)] = { translation };
  }
  return resources;
}

function readStoredCulture(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(USER_PREFERRED_CULTURE_STORAGE_KEY);
    if (raw === null || raw === '') {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === 'string' && parsed !== '' ? parsed : null;
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
}

function persistCultureIfStorageIsWritable(culture: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(USER_PREFERRED_CULTURE_STORAGE_KEY, JSON.stringify(culture));
  } catch {
    return;
  }
}

function detectBrowserCulture(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return DEFAULT_CULTURE;
}

function toDayjsLocale(culture: string): string {
  const normalized = culture.toLowerCase();
  if (DAYJS_LOCALES.has(normalized)) {
    return normalized;
  }
  const [language = normalized] = normalized.split('-');
  return DAYJS_LOCALES.has(language) ? language : FALLBACK_LANGUAGE;
}

function applyCultureSideEffects(culture: string): void {
  dayjs.locale(toDayjsLocale(culture));
  if (typeof document !== 'undefined') {
    document.documentElement.lang = culture;
  }
}

export async function initI18n(): Promise<void> {
  const culture = readStoredCulture() ?? detectBrowserCulture();
  await i18next
    .use(ICU)
    .use(initReactI18next)
    .init({
      resources: buildResources(),
      lng: culture,
      fallbackLng: FALLBACK_LANGUAGE,
      keySeparator: false,
      nsSeparator: false,
      interpolation: {
        escapeValue: false,
      },
    });
  applyCultureSideEffects(culture);
}

export async function changeLanguage(lang: string): Promise<void> {
  persistCultureIfStorageIsWritable(lang);
  await i18next.changeLanguage(lang);
  applyCultureSideEffects(lang);
}
